import { useState, useRef, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { trackEvent } from "@/hooks/use-activity-tracker";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useLocation } from "wouter";
import { apiRequest } from "@/lib/queryClient";
import { useDean } from "@/contexts/dean-context";
import { canReadDeanConversations, deanComposerState, deanMembershipMessage, sendDeanConversation } from "@/lib/deanChatSession";
import { useDeanAccountAccess } from "@/hooks/use-dean-account-access";
import { DeanDealershipNotice } from "@/components/dean-dealership-notice";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Skeleton } from "@/components/ui/skeleton";
import { useToast } from "@/hooks/use-toast";
import {
  beginDeanExchange,
  clearDeanExchange,
  conversationHasAnswerForExchange,
  DEAN_RECONCILE_INTERVAL_MS,
  DEAN_RECONCILE_TIMEOUT_MS,
  DeanStreamInterruptedError,
  getDeanPendingExchange,
  getDeanRuntimeExchange,
  getDeanSessionRevision,
  hasDeanSession,
  isDeanNewChat,
  hasUnresolvedDeanExchange,
  readRememberedDeanConversation,
  rememberDeanConversation,
  shouldPollDeanConversation,
  streamDeanMessage,
  updateDeanExchange,
  updateDeanPendingExchange,
  useDeanLifecycleRevision,
} from "@/lib/deanChatLifecycle";
import { hasDeanMarkdownTable, renderDeanMarkdown } from "@/lib/deanMarkdown";
import {
  Plus, Trash2, Send, Bot, User, Paperclip, Sparkles,
  FileText, ChevronRight, Upload, Loader2, X, ThumbsUp, Copy, Check, AlertCircle,
} from "lucide-react";

type Conversation = {
  id: number;
  title: string;
  documentContext?: string | null;
  documentType?: string | null;
  createdAt: string;
  updatedAt?: string | null;
};

type Message = {
  id: number;
  exchangeId?: string | null;
  conversationId: number;
  role: "user" | "assistant" | "system";
  content: string;
  createdAt: string;
};

type ConversationWithMessages = Conversation & { messages: Message[] };

const SUGGESTED_PROMPT_KEYS = [
  "suggestedPrompts.floorplanCost",
  "suggestedPrompts.prioritizeSelling",
  "suggestedPrompts.breakeven",
  "suggestedPrompts.negotiateRates",
  "suggestedPrompts.carryingCost",
  "suggestedPrompts.agingRisk",
];

// Session-only bookmarks for the floating panel. This intentionally lives in
// memory rather than local/session storage: closing DEAN preserves the reader's
// place, while a full page reload starts fresh.
const deanConversationScrollPositions = new Map<number, number>();

function MessageBubble({ message, onThumbsUp, thumbsUpSent }: { message: Message; onThumbsUp?: (messageId: number) => void; thumbsUpSent?: boolean }) {
  const { t } = useTranslation(["dean", "common"]);
  const [copied, setCopied] = useState(false);

  if (message.role === "system") {
    return (
      <div className="flex items-center gap-2 text-xs text-muted-foreground py-1 px-2" data-testid={`system-message-${message.id}`}>
        <FileText className="w-3 h-3 shrink-0" />
        <span>{message.content}</span>
      </div>
    );
  }

  const isUser = message.role === "user";

  function handleCopy() {
    navigator.clipboard.writeText(message.content).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }

  return (
    <div className={`flex w-full min-w-0 max-w-full gap-3 overflow-hidden ${isUser ? "flex-row-reverse" : "flex-row"}`} data-testid={`message-${message.id}`}>
      <div className={`w-7 h-7 rounded-full flex items-center justify-center shrink-0 mt-0.5 ${isUser ? "bg-primary" : "bg-primary/10"}`}>
        {isUser ? (
          <User className="w-3.5 h-3.5 text-primary-foreground" />
        ) : (
          <Bot className="w-3.5 h-3.5 text-primary" />
        )}
      </div>
      <div className={`flex flex-col gap-1 ${!isUser && hasDeanMarkdownTable(message.content) ? "w-0 flex-1 min-w-0 max-w-full" : "min-w-0 max-w-[80%]"}`}>
        <div className={`min-w-0 max-w-full overflow-hidden rounded-2xl px-4 py-2.5 text-sm ${isUser
          ? "bg-primary text-primary-foreground"
          : "bg-muted text-foreground"
        }`}>
          {isUser ? (
            <p className="whitespace-pre-wrap">{message.content}</p>
          ) : (
            <div
              className="prose-sm min-w-0 max-w-full break-words [overflow-wrap:anywhere] leading-relaxed"
              dangerouslySetInnerHTML={{ __html: renderDeanMarkdown(message.content) }}
            />
          )}
        </div>
        {!isUser && (
          <div className="self-start flex items-center gap-1">
            <button
              className={`flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full transition-colors ${copied ? "text-primary bg-primary/10" : "text-muted-foreground hover:text-primary hover:bg-primary/5"}`}
              onClick={handleCopy}
              data-testid={`button-copy-${message.id}`}
            >
              {copied ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
              {copied ? t("dean:message.copied") : t("dean:message.copy")}
            </button>
            {onThumbsUp && (
              <button
                className={`flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full transition-colors ${thumbsUpSent ? "text-primary bg-primary/10" : "text-muted-foreground hover:text-primary hover:bg-primary/5"}`}
                onClick={() => onThumbsUp(message.id)}
                disabled={thumbsUpSent}
                data-testid={`button-thumbs-up-${message.id}`}
                title={t("dean:message.helpfulTooltip")}
              >
                <ThumbsUp className="w-3 h-3" />
                {thumbsUpSent ? t("dean:message.helpfulSaved") : t("dean:message.helpful")}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function StreamingBubble({ content }: { content: string }) {
  return (
    <div className="flex w-full min-w-0 max-w-full gap-3 overflow-hidden">
      <div className="w-7 h-7 rounded-full flex items-center justify-center shrink-0 mt-0.5 bg-primary/10">
        <Bot className="w-3.5 h-3.5 text-primary" />
      </div>
      <div className="min-w-0 max-w-[80%] overflow-hidden rounded-2xl px-4 py-2.5 text-sm bg-muted text-foreground">
        {content ? (
          <div
            className="prose-sm min-w-0 max-w-full break-words [overflow-wrap:anywhere] leading-relaxed"
            dangerouslySetInnerHTML={{ __html: renderDeanMarkdown(content) }}
          />
        ) : (
          <div className="flex items-center gap-1.5 h-5">
            <span className="w-1.5 h-1.5 rounded-full bg-primary/60 animate-bounce" style={{ animationDelay: "0ms" }} />
            <span className="w-1.5 h-1.5 rounded-full bg-primary/60 animate-bounce" style={{ animationDelay: "150ms" }} />
            <span className="w-1.5 h-1.5 rounded-full bg-primary/60 animate-bounce" style={{ animationDelay: "300ms" }} />
          </div>
        )}
      </div>
    </div>
  );
}

function ConversationList({
  conversations,
  activeId,
  onSelect,
  onNew,
  onDelete,
  isLoading,
}: {
  conversations: Conversation[];
  activeId: number | null;
  onSelect: (id: number) => void;
  onNew: () => void;
  onDelete: (id: number) => void;
  isLoading: boolean;
}) {
  const { t } = useTranslation(["dean", "common"]);
  return (
    <div className="flex flex-col h-full border-r">
      <div className="p-2 border-b shrink-0">
        <Button
          onClick={onNew}
          className="w-full gap-2"
          data-testid="button-new-chat"
          size="sm"
          variant="outline"
        >
          <Plus className="w-3.5 h-3.5" />
          {t("dean:newChat")}
        </Button>
      </div>
      <ScrollArea className="flex-1">
        <div className="p-1.5 space-y-0.5">
          {isLoading && (
            <div className="space-y-1 p-1">
              <Skeleton className="h-8 w-full rounded-lg" />
              <Skeleton className="h-8 w-full rounded-lg" />
              <Skeleton className="h-8 w-full rounded-lg" />
            </div>
          )}
          {!isLoading && conversations.length === 0 && (
            <div className="text-center text-[10px] text-muted-foreground py-6 px-2">
              {t("dean:sidebar.noChats")}
              <br />{t("dean:sidebar.startNewChat")}
            </div>
          )}
          {conversations.map((conv) => (
            <div
              key={conv.id}
              className={`group flex items-center gap-1.5 rounded-lg px-2 py-1.5 cursor-pointer transition-colors text-xs ${
                activeId === conv.id
                  ? "bg-primary/10 text-primary"
                  : "hover:bg-muted text-foreground"
              }`}
              onClick={() => onSelect(conv.id)}
              data-testid={`conversation-item-${conv.id}`}
            >
              {conv.documentType && conv.documentType !== "other" ? (
                <FileText className="w-3 h-3 shrink-0 text-muted-foreground" />
              ) : (
                <Sparkles className="w-3 h-3 shrink-0 text-muted-foreground" />
              )}
              <span className="flex-1 truncate min-w-0 leading-tight">{conv.title}</span>
              <Button
                variant="ghost"
                size="icon"
                className="h-5 w-5 opacity-0 group-hover:opacity-100 shrink-0 text-muted-foreground hover:text-destructive"
                onClick={(e) => { e.stopPropagation(); onDelete(conv.id); }}
                data-testid={`button-delete-conversation-${conv.id}`}
                title={t("dean:sidebar.deleteConversation")}
              >
                <Trash2 className="w-2.5 h-2.5" />
              </Button>
            </div>
          ))}
        </div>
      </ScrollArea>
    </div>
  );
}

interface AiAssistantPanelProps {
  open: boolean;
  onClose: () => void;
  initialMessage?: string;
  conversationId?: number | null;
}

export function AiAssistantPanel({ open, onClose, initialMessage, conversationId: propConversationId }: AiAssistantPanelProps) {
  const { clearRequestedConversation } = useDean();
  const { missingDealership, canImpersonate, viewOnly, openDealerPicker } = useDeanAccountAccess();
  const { t } = useTranslation(["dean", "common"]);
  const { toast } = useToast();
  const [, navigate] = useLocation();
  const qc = useQueryClient();
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesScrollAreaRef = useRef<HTMLDivElement>(null);
  const streamingBubbleRef = useRef<HTMLDivElement>(null);
  const pendingBubbleRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [activeConversationId, setActiveConversationIdState] = useState<number | null>(
    readRememberedDeanConversation,
  );
  const activeConversationRef = useRef(activeConversationId);
  const [sendError, setSendError] = useState<string | null>(null);
  const [input, setInput] = useState("");
  const [isCreatingConversation, setIsCreatingConversation] = useState(false);
  const [uploadingDoc, setUploadingDoc] = useState(false);
  const [extracting, setExtracting] = useState(false);
  const [knownDocType, setKnownDocType] = useState<string | null>(null);
  const [showExtractionPrompt, setShowExtractionPrompt] = useState(false);
  const [showRoutingPrompt, setShowRoutingPrompt] = useState(false);
  const [thumbsUpSent, setThumbsUpSent] = useState<Set<number>>(new Set());
  const [hasAutoSelectedMostRecent, setHasAutoSelectedMostRecent] = useState(isDeanNewChat);
  useDeanLifecycleRevision();

  const runtimeExchange = getDeanRuntimeExchange(activeConversationId);
  const pendingExchange = getDeanPendingExchange(activeConversationId);
  const isStreaming = runtimeExchange?.status === "streaming"
    || runtimeExchange?.status === "reconciling";
  const showRuntimeAnswer = Boolean(runtimeExchange && runtimeExchange.status !== "failed");
  const hasFailedExchange = runtimeExchange?.status === "failed"
    || (!runtimeExchange && pendingExchange?.status === "failed");
  const hasUnresolvedExchange = activeConversationId != null
    && hasUnresolvedDeanExchange(activeConversationId);
  const pendingUserMessage = runtimeExchange?.question ?? null;
  const streamingContent = runtimeExchange?.answer ?? "";

  const setActiveConversationId = useCallback((conversationId: number | null) => {
    activeConversationRef.current = conversationId;
    setActiveConversationIdState(conversationId);
    rememberDeanConversation(conversationId);
  }, []);

  const { data: conversations = [], isLoading: loadingConversations, error: conversationsError } = useQuery<Conversation[]>({
    queryKey: ["/api/conversations"],
    enabled: (query) => open && !sendError && canReadDeanConversations(hasDeanSession(), missingDealership, query.state.error),
    retry: false,
    refetchInterval: (query) => canReadDeanConversations(hasDeanSession(), missingDealership, query.state.error)
      ? 5 * 60 * 1000 : false,
    refetchOnMount: false,
  });

  // Auto-select most recent conversation when panel opens with no specific conversationId
  useEffect(() => {
    if (!open || missingDealership || deanMembershipMessage(conversationsError) || propConversationId != null || hasAutoSelectedMostRecent) return;
    if (activeConversationId != null && conversations.some((conversation) => conversation.id === activeConversationId)) {
      setHasAutoSelectedMostRecent(true);
      return;
    }
    if (conversations.length > 0) {
      setActiveConversationId(conversations[0].id);
      setHasAutoSelectedMostRecent(true);
    }
  }, [open, missingDealership, conversationsError, propConversationId, conversations, hasAutoSelectedMostRecent, activeConversationId, setActiveConversationId]);

  // When a specific conversationId prop is provided, pre-select it
  useEffect(() => {
    if (open && !missingDealership && propConversationId != null) {
      setActiveConversationId(propConversationId);
      setHasAutoSelectedMostRecent(true);
    }
  }, [open, propConversationId, missingDealership]);

  // Reset auto-select flag when panel closes so next open re-runs auto-select
  useEffect(() => {
    if (!open) {
      setHasAutoSelectedMostRecent(isDeanNewChat());
    }
  }, [open]);

  // Track AI assistant panel opens
  useEffect(() => {
    if (open) {
      trackEvent("ai_assistant_opened");
    }
  }, [open]);

  // When panel opens with an initialMessage, create a conversation and send it
  useEffect(() => {
    if (open && hasDeanSession() && !missingDealership && !viewOnly && !deanMembershipMessage(conversationsError) && initialMessage && !activeConversationId && !isCreatingConversation) {
      setHasAutoSelectedMostRecent(true);
      createAndSendFirst(initialMessage);
    }
  }, [open, initialMessage, missingDealership, viewOnly]);

  const { data: activeConversation, isLoading: loadingConversation, error: conversationError } = useQuery<ConversationWithMessages>({
    queryKey: ["/api/conversations", activeConversationId],
    enabled: (query) => activeConversationId !== null && !sendError && !deanMembershipMessage(conversationsError)
      && canReadDeanConversations(hasDeanSession(), missingDealership, query.state.error),
    retry: false,
    refetchInterval: (query) => canReadDeanConversations(hasDeanSession(), missingDealership, query.state.error)
      && !deanMembershipMessage(conversationsError) && shouldPollDeanConversation(
      query.state.data as ConversationWithMessages | undefined,
      getDeanPendingExchange(activeConversationId),
      getDeanRuntimeExchange(activeConversationId)?.status === "streaming",
    ) ? DEAN_RECONCILE_INTERVAL_MS : false,
  });

  const membershipMessage = deanMembershipMessage(conversationsError)
    ?? deanMembershipMessage(conversationError) ?? sendError;
  const needsDealership = missingDealership || !!membershipMessage;
  const discardConversation = useCallback((id: number) => {
    clearRequestedConversation();
    clearDeanExchange(id);
    qc.removeQueries({ queryKey: ["/api/conversations", id] });
    setActiveConversationId(null);
    setHasAutoSelectedMostRecent(true);
  }, [qc, setActiveConversationId, clearRequestedConversation]);

  useEffect(() => {
    if (activeConversationId !== null && (
      (conversationError as { status?: number } | null)?.status === 404 || needsDealership
    )) discardConversation(activeConversationId);
  }, [conversationError, needsDealership, activeConversationId, discardConversation]);

  useEffect(() => {
    if (!activeConversationId) return;
    const exchange = getDeanRuntimeExchange(activeConversationId);
    const pending = getDeanPendingExchange(activeConversationId);
    if (conversationHasAnswerForExchange(activeConversation, exchange ?? pending)) {
      clearDeanExchange(activeConversationId, exchange?.requestId);
    }
  }, [activeConversationId, activeConversation]);

  useEffect(() => {
    if (!activeConversationId || runtimeExchange?.status === "streaming") return;
    const exchange = runtimeExchange ?? pendingExchange;
    if (!exchange || exchange.status === "failed") return;
    const remaining = Math.max(0, exchange.startedAt + DEAN_RECONCILE_TIMEOUT_MS - Date.now());
    const timeout = window.setTimeout(() => {
      const latestRuntime = getDeanRuntimeExchange(activeConversationId);
      if (latestRuntime) {
        updateDeanExchange(activeConversationId, latestRuntime.requestId, { status: "failed" });
      } else {
        updateDeanPendingExchange(activeConversationId, "failed");
      }
    }, remaining);
    return () => window.clearTimeout(timeout);
  }, [
    activeConversationId,
    runtimeExchange?.requestId,
    runtimeExchange?.status,
    runtimeExchange?.startedAt,
    pendingExchange?.status,
    pendingExchange?.startedAt,
  ]);

  // Create a conversation in the DB and return its ID
  const createConversationRecord = useCallback(async (): Promise<number> => {
    if (!hasDeanSession() || missingDealership || viewOnly) throw new DOMException("DEAN context unavailable", "AbortError");
    const revision = getDeanSessionRevision();
    const res = await apiRequest("POST", "/api/conversations", { title: "New Chat" });
    const conv = await res.json() as Conversation;
    if (revision !== getDeanSessionRevision()) throw new DOMException("Account changed", "AbortError");
    qc.setQueryData<ConversationWithMessages>(
      ["/api/conversations", conv.id],
      { ...conv, messages: [] },
    );
    qc.invalidateQueries({ queryKey: ["/api/conversations"] });
    setActiveConversationId(conv.id);
    return conv.id;
  }, [qc, setActiveConversationId, missingDealership, viewOnly]);

  const sendMessageToConversation = useCallback(async (conversationId: number, content: string) => {
    const revision = getDeanSessionRevision();
    if (!content.trim()) return;
    if (hasUnresolvedDeanExchange(conversationId)) return;
    const existing = getDeanRuntimeExchange(conversationId);
    const persisted = getDeanPendingExchange(conversationId);
    if (existing || persisted) clearDeanExchange(conversationId, existing?.requestId);

    setInput("");
    setActiveConversationId(conversationId);
    const exchange = beginDeanExchange(conversationId, content);
    const optimisticMessage: Message = {
      id: -exchange.requestId,
      conversationId,
      role: "user",
      content,
      createdAt: new Date(exchange.startedAt).toISOString(),
    };
    qc.setQueryData<ConversationWithMessages>(
      ["/api/conversations", conversationId],
      (current) => current
        ? { ...current, messages: [...current.messages, optimisticMessage] }
        : current,
    );

    try {
      await streamDeanMessage({
        conversationId,
        exchangeId: exchange.exchangeId,
        content,
        onAccepted: () => {
          if (revision !== getDeanSessionRevision()) return;
          void qc.invalidateQueries({ queryKey: ["/api/conversations", conversationId] });
          void qc.invalidateQueries({ queryKey: ["/api/conversations"] });
        },
        onContent: (answer) => {
          updateDeanExchange(conversationId, exchange.requestId, { answer });
        },
      });
      if (revision !== getDeanSessionRevision()) return;
      updateDeanExchange(conversationId, exchange.requestId, { status: "complete" });
      await qc.invalidateQueries({ queryKey: ["/api/conversations", conversationId] });
      void qc.invalidateQueries({ queryKey: ["/api/conversations"] });
    } catch (err: any) {
      if (revision !== getDeanSessionRevision()) return;
      if (err?.status === 404 || deanMembershipMessage(err)) {
        clearDeanExchange(conversationId, exchange.requestId);
        throw err;
      }
      const interrupted = err instanceof DeanStreamInterruptedError
        || err?.name === "TypeError";
      updateDeanExchange(
        conversationId,
        exchange.requestId,
        { status: interrupted ? "reconciling" : "failed" },
      );
      await qc.invalidateQueries({ queryKey: ["/api/conversations", conversationId] });
      void qc.invalidateQueries({ queryKey: ["/api/conversations"] });
      if (!interrupted) {
        toast({ title: t("dean:toast.sendFailed"), description: err.message, variant: "destructive" });
      }
    }
  }, [qc, setActiveConversationId, toast, t]);

  // Create conversation + send first message in one flow
  const createAndSendFirst = useCallback(async (content: string) => {
    if (!content.trim() || isCreatingConversation) return;
    setIsCreatingConversation(true);
    try {
      const convId = await createConversationRecord();
      setIsCreatingConversation(false);
      await sendMessageToConversation(convId, content);
    } catch (err: any) {
      if (err?.name === "AbortError") return;
      setIsCreatingConversation(false);
      const message = deanMembershipMessage(err);
      setSendError(message);
      toast({ title: t("dean:toast.startFailed"), description: message ?? err.message, variant: "destructive" });
    } finally { setIsCreatingConversation(false); }
  }, [createConversationRecord, isCreatingConversation, sendMessageToConversation, t, toast]);

  const sendMessage = useCallback(async (content: string) => {
    if (!content.trim() || !hasDeanSession() || isStreaming || isCreatingConversation || needsDealership || viewOnly) return;
    setIsCreatingConversation(true);
    try {
      await sendDeanConversation({
        activeId: () => activeConversationRef.current,
        create: createConversationRecord,
        send: (id) => sendMessageToConversation(id, content),
        discard: discardConversation,
      });
    } catch (err: any) {
      if (err?.name === "AbortError") return;
      const message = deanMembershipMessage(err);
      setSendError(message);
      toast({ title: t("dean:toast.sendFailed"), description: message ?? err.message, variant: "destructive" });
    } finally { setIsCreatingConversation(false); }
  }, [isStreaming, isCreatingConversation, needsDealership, viewOnly, createConversationRecord, sendMessageToConversation, discardConversation, toast, t]);

  const handleThumbsUp = useCallback(async (messageId: number) => {
    if (!activeConversationId || thumbsUpSent.has(messageId)) return;
    setThumbsUpSent((prev) => new Set(prev).add(messageId));
    try {
      await fetch(`/api/conversations/${activeConversationId}/messages/${messageId}/thumbs-up`, {
        method: "POST",
        credentials: "include",
      });
    } catch {
      // Non-fatal — UI already updated optimistically
    }
  }, [activeConversationId, thumbsUpSent]);

  const handleSend = () => {
    if (input.trim()) sendMessage(input.trim());
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleSuggestedPrompt = (prompt: string) => {
    sendMessage(prompt);
  };

  const handleNewChat = () => {
    clearRequestedConversation();
    setActiveConversationId(null);
    setSendError(null);
    setHasAutoSelectedMostRecent(true);
    setInput("");
    setShowExtractionPrompt(false);
    setShowRoutingPrompt(false);
    setKnownDocType(null);
  };

  const handleDeleteConversation = async (id: number) => {
    try {
      await apiRequest("DELETE", `/api/conversations/${id}`);
      qc.invalidateQueries({ queryKey: ["/api/conversations"] });
      if (activeConversationId === id) {
        setActiveConversationId(null);
      }
    } catch {
      toast({ title: t("dean:toast.deleteFailed"), variant: "destructive" });
    }
  };

  const handleDocumentUpload = async (file: File) => {
    let convId = activeConversationId;
    if (!convId) {
      try {
        convId = await createConversationRecord();
      } catch {
        toast({ title: t("dean:toast.selectConversationFirst"), variant: "destructive" });
        return;
      }
    }

    setUploadingDoc(true);
    setShowExtractionPrompt(false);
    setShowRoutingPrompt(false);
    setKnownDocType(null);

    try {
      const formData = new FormData();
      formData.append("file", file);

      const res = await fetch(`/api/conversations/${convId}/document`, {
        method: "POST",
        body: formData,
        credentials: "include",
      });

      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.error || "Upload failed");
      }

      const result = await res.json();
      qc.invalidateQueries({ queryKey: ["/api/conversations", convId] });
      qc.invalidateQueries({ queryKey: ["/api/conversations"] });

      if (result.isKnownType) {
        setKnownDocType(result.documentType);
        setShowExtractionPrompt(true);
      }

      toast({ title: t("dean:toast.documentUploaded"), description: t("dean:toast.documentUploadedDesc") });
      setShowRoutingPrompt(true);
    } catch (err: any) {
      toast({ title: t("dean:toast.uploadFailed"), description: err.message, variant: "destructive" });
    } finally {
      setUploadingDoc(false);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) handleDocumentUpload(file);
    e.target.value = "";
  };

  // Preserve the exact viewport for each conversation while this app page
  // remains mounted. AiAssistantPanel itself is unmounted when the floating
  // panel closes, so the module-level map carries the bookmark across reopen.
  useEffect(() => {
    if (!open || activeConversationId == null || loadingConversation) return;
    const viewport = messagesScrollAreaRef.current?.querySelector<HTMLElement>(
      "[data-radix-scroll-area-viewport]",
    );
    if (!viewport) return;

    const savedPosition = deanConversationScrollPositions.get(activeConversationId);
    if (savedPosition != null) {
      viewport.scrollTop = savedPosition;
    }

    const rememberPosition = () => {
      deanConversationScrollPositions.set(activeConversationId, viewport.scrollTop);
    };
    viewport.addEventListener("scroll", rememberPosition, { passive: true });
    return () => {
      rememberPosition();
      viewport.removeEventListener("scroll", rememberPosition);
    };
  }, [open, activeConversationId, loadingConversation]);

  // Scroll to the bottom when the conversation changes or when the user sends
  // a message (so their just-sent bubble is visible). While DEAN is streaming
  // we intentionally do NOT keep pinning to the bottom — instead, when the
  // answer starts, we scroll DEAN's bubble so it starts at the top of view.
  const prevScrolledConvRef = useRef<number | null>(null);
  useEffect(() => {
    if (loadingConversation) return;
    const convChanged = prevScrolledConvRef.current !== activeConversationId;
    if (convChanged) prevScrolledConvRef.current = activeConversationId;
    const hasSavedPosition = activeConversationId != null
      && deanConversationScrollPositions.has(activeConversationId);
    // Only scroll to bottom when opening a conversation or right after the
    // user sends a message — never when pendingUserMessage CLEARS (that
    // happens when a long answer finishes, and yanking to the bottom then
    // would pull the reader away from the top of DEAN's answer).
    if ((convChanged && !hasSavedPosition) || pendingUserMessage) {
      messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  }, [activeConversationId, pendingUserMessage, loadingConversation]);

  const hasScrolledToStreamRef = useRef(false);
  useEffect(() => {
    if (!showRuntimeAnswer) {
      hasScrolledToStreamRef.current = false;
      return;
    }
    if (streamingContent && !hasScrolledToStreamRef.current) {
      hasScrolledToStreamRef.current = true;
      const el = streamingBubbleRef.current;
      if (el) {
        // Keep up to ~3 lines of the user's question visible above the answer.
        const questionEl = pendingBubbleRef.current;
        const questionPeek = questionEl
          ? Math.min(questionEl.offsetHeight, 84) + 16
          : 8;
        el.style.scrollMarginTop = `${questionPeek}px`;
        el.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    }
  }, [showRuntimeAnswer, streamingContent]);

  const messages = activeConversation?.messages ?? [];
  const hasMessages = messages.filter((m) => m.role !== "system").length > 0;
  const { busy: isPending, disabled: composerDisabled } = deanComposerState({
    session: hasDeanSession(), needsDealership, viewOnly,
    creating: isCreatingConversation, unresolved: hasUnresolvedExchange, uploading: uploadingDoc,
  });

  if (!open) return null;

  return (
    <div className="flex h-full" data-testid="ai-assistant-panel">
      {/* Sidebar: conversation list */}
      <div className="w-36 shrink-0 flex flex-col overflow-hidden">
        <ConversationList
          conversations={conversations}
          activeId={activeConversationId}
          onSelect={(id) => {
            setActiveConversationId(id);
            setHasAutoSelectedMostRecent(true);
            setShowExtractionPrompt(false);
            setShowRoutingPrompt(false);
            setKnownDocType(null);
          }}
          onNew={handleNewChat}
          onDelete={handleDeleteConversation}
          isLoading={loadingConversations}
        />
      </div>

      {/* Main chat area */}
      <div className="flex flex-col flex-1 min-w-0 h-full">
        {/* Top bar */}
        <div className="flex items-center gap-3 px-3 py-2.5 border-b shrink-0">
          <div className="flex items-center gap-2 flex-1 min-w-0">
            <div className="min-w-0">
              <p className="text-xs font-semibold leading-tight truncate">
                {activeConversation ? activeConversation.title : t("dean:newChat")}
              </p>
              {activeConversation?.documentType && activeConversation.documentType !== "other" && (
                <span className="text-[10px] text-muted-foreground">
                  {activeConversation.documentType === "floorplan_statement" ? `📄 ${t("dean:docBadge.floorplan")}` : `📄 ${t("dean:docBadge.receivable")}`}
                </span>
              )}
            </div>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
            className="shrink-0 h-7 w-7"
            data-testid="button-close-ai-panel"
          >
            <X className="w-3.5 h-3.5" />
          </Button>
        </div>

        {/* Messages area */}
        <ScrollArea ref={messagesScrollAreaRef} className="flex-1 px-3 py-3" data-testid="dean-chat-area">
          {needsDealership && <DeanDealershipNotice canImpersonate={canImpersonate} onViewAsDealer={openDealerPicker} />}
          {!needsDealership && !activeConversationId && !isCreatingConversation && (
            <div className="flex flex-col items-center gap-4 py-4" data-testid="suggested-prompts">
              <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                <Sparkles className="w-5 h-5 text-primary" />
              </div>
              <div className="text-center">
                <p className="text-xs font-medium mb-1">{t("dean:empty.titleShort")}</p>
                <p className="text-[10px] text-muted-foreground">{t("dean:empty.subtitleShort")}</p>
              </div>
              <div className="grid grid-cols-1 gap-1.5 w-full">
                {SUGGESTED_PROMPT_KEYS.slice(0, 4).map((promptKey) => {
                  const prompt = t(`dean:${promptKey}`);
                  return (
                    <button
                      key={promptKey}
                      onClick={() => handleSuggestedPrompt(prompt)}
                      className="text-left text-[11px] px-2.5 py-2 rounded-lg border hover:border-primary/40 hover:bg-primary/5 transition-colors flex items-center gap-1.5 group"
                      data-testid={`suggested-prompt-${promptKey.split(".").pop()}`}
                    >
                      <ChevronRight className="w-3 h-3 text-primary shrink-0 opacity-0 group-hover:opacity-100 transition-opacity" />
                      <span>{prompt}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {!needsDealership && isCreatingConversation && !activeConversationId && (
            <div className="flex flex-col items-center justify-center h-full min-h-[200px] gap-3" data-testid="creating-conversation">
              <Loader2 className="w-5 h-5 animate-spin text-primary/60" />
              <p className="text-xs text-muted-foreground">{t("dean:creatingShort")}</p>
            </div>
          )}

          {!needsDealership && activeConversationId && loadingConversation && (
            <div className="space-y-3">
              <div className="flex gap-2">
                <Skeleton className="w-6 h-6 rounded-full shrink-0" />
                <Skeleton className="h-12 w-48 rounded-2xl" />
              </div>
            </div>
          )}

          {!needsDealership && activeConversationId && !loadingConversation && !hasMessages && !showRuntimeAnswer && !hasFailedExchange && (
            <div className="flex flex-col items-center gap-3 py-4" data-testid="suggested-prompts-loaded">
              <div className="text-center">
                <p className="text-xs font-medium mb-1">{t("dean:empty.titleShort")}</p>
              </div>
              <div className="grid grid-cols-1 gap-1.5 w-full">
                {SUGGESTED_PROMPT_KEYS.slice(0, 4).map((promptKey) => {
                  const prompt = t(`dean:${promptKey}`);
                  return (
                    <button
                      key={promptKey}
                      onClick={() => handleSuggestedPrompt(prompt)}
                      className="text-left text-[11px] px-2.5 py-2 rounded-lg border hover:border-primary/40 hover:bg-primary/5 transition-colors flex items-center gap-1.5 group"
                      data-testid={`suggested-prompt-${promptKey.split(".").pop()}`}
                    >
                      <ChevronRight className="w-3 h-3 text-primary shrink-0 opacity-0 group-hover:opacity-100 transition-opacity" />
                      <span>{prompt}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {activeConversationId && !loadingConversation && (
            <div className="space-y-3">
              {messages.map((msg) => (
                <MessageBubble
                  key={msg.id}
                  message={msg}
                  onThumbsUp={msg.role === "assistant" ? handleThumbsUp : undefined}
                  thumbsUpSent={thumbsUpSent.has(msg.id)}
                />
              ))}
              {pendingUserMessage && !(messages.length > 0 && messages[messages.length - 1].role === "user" && messages[messages.length - 1].content === pendingUserMessage) && (
                <div ref={pendingBubbleRef} className="flex gap-3 flex-row-reverse" data-testid="pending-user-message">
                  <div className="w-7 h-7 rounded-full flex items-center justify-center shrink-0 mt-0.5 bg-primary">
                    <User className="w-3.5 h-3.5 text-primary-foreground" />
                  </div>
                  <div className="flex flex-col gap-1 max-w-[80%]">
                    <div className="rounded-2xl px-4 py-2.5 text-sm bg-primary text-primary-foreground">
                      <p className="whitespace-pre-wrap">{pendingUserMessage}</p>
                    </div>
                  </div>
                </div>
              )}
              {showRuntimeAnswer && (
                <div
                  ref={streamingBubbleRef}
                  style={{ scrollMarginTop: "8px" }}
                  data-testid="dean-streaming-answer"
                >
                  <StreamingBubble content={streamingContent || ""} />
                </div>
              )}
              {hasFailedExchange && !membershipMessage && (
                <div className="flex gap-2" data-testid="dean-answer-interrupted">
                  <div className="w-6 h-6 rounded-full flex items-center justify-center shrink-0 mt-0.5 bg-destructive/10">
                    <AlertCircle className="w-3 h-3 text-destructive" />
                  </div>
                  <div className="max-w-[85%] rounded-2xl px-3 py-2.5 text-xs border border-destructive/25 bg-destructive/5 space-y-2">
                    <p>{t("dean:recovery.interrupted")}</p>
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-7 text-xs"
                      onClick={() => {
                        if (runtimeExchange?.question) setInput(runtimeExchange.question);
                        if (activeConversationId) {
                          clearDeanExchange(activeConversationId, runtimeExchange?.requestId);
                        }
                        textareaRef.current?.focus();
                      }}
                      data-testid="button-retry-dean-question"
                    >
                      {t(runtimeExchange ? "dean:recovery.tryAgain" : "dean:recovery.dismissAndRetry")}
                    </Button>
                  </div>
                </div>
              )}

              {/* Smart routing prompt */}
              {showRoutingPrompt && !isStreaming && (
                <div className="flex gap-2" data-testid="routing-prompt-card">
                  <div className="w-6 h-6 rounded-full flex items-center justify-center shrink-0 mt-0.5 bg-primary/10">
                    <Bot className="w-3 h-3 text-primary" />
                  </div>
                  <div className="max-w-[85%] rounded-2xl px-3 py-2.5 text-xs bg-muted text-foreground space-y-2">
                    <p>{t("dean:routing.question")}</p>
                    <div className="flex gap-1.5">
                      <Button
                        size="sm"
                        className="h-7 text-xs px-2.5"
                        onClick={async () => {
                          setShowRoutingPrompt(false);
                          setExtracting(true);
                          try {
                            const res = await fetch(`/api/conversations/${activeConversationId}/extract`, {
                              method: "POST",
                              credentials: "include",
                            });
                            if (!res.ok) {
                              const err = await res.json();
                              throw new Error(err.error || "Extraction failed");
                            }
                            const result = await res.json();
                            qc.invalidateQueries({ queryKey: ["/api/conversations", activeConversationId] });
                            const docLabel = result.documentType === "receivable_report" ? t("dean:docLabel.receivableReport") : t("dean:docLabel.statement");
                            toast({
                              title: t("dean:toast.savedToPlatform"),
                              description: t("dean:toast.savedToPlatformDesc", { doc: docLabel, lender: result.lenderName ? ` (${result.lenderName})` : "" }),
                            });
                            setShowExtractionPrompt(false);
                            onClose();
                            navigate("/floorplan");
                          } catch (err: any) {
                            toast({ title: t("dean:toast.placeFailed"), description: err.message, variant: "destructive" });
                          } finally {
                            setExtracting(false);
                          }
                        }}
                        disabled={extracting}
                        data-testid="button-routing-yes"
                      >
                        {extracting ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : null}
                        {t("dean:routing.yes")}
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-7 text-xs px-2.5"
                        onClick={() => { setShowRoutingPrompt(false); setShowExtractionPrompt(false); }}
                        data-testid="button-routing-no"
                      >
                        {t("dean:routing.no")}
                      </Button>
                    </div>
                  </div>
                </div>
              )}

              <div ref={messagesEndRef} />
            </div>
          )}
        </ScrollArea>

        {/* Extraction prompt banner */}
        {showExtractionPrompt && !showRoutingPrompt && knownDocType && activeConversationId && (
          <div className="mx-2 mb-1 p-2 rounded-lg border border-primary/30 bg-primary/5 flex items-center gap-2" data-testid="extraction-offer-banner">
            <Upload className="w-3.5 h-3.5 text-primary shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-xs font-medium leading-tight">
                {knownDocType === "floorplan_statement" ? t("dean:extraction.floorplanDetected") : t("dean:extraction.receivableDetected")}
              </p>
            </div>
            <div className="flex items-center gap-1 shrink-0">
              <Button size="sm" variant="ghost" className="h-6 text-xs px-2" onClick={() => setShowExtractionPrompt(false)} data-testid="button-dismiss-extraction">
                {t("dean:extraction.dismiss")}
              </Button>
              <Button
                size="sm"
                className="h-6 text-xs px-2"
                disabled={extracting}
                onClick={async () => {
                  setExtracting(true);
                  try {
                    const res = await fetch(`/api/conversations/${activeConversationId}/extract`, {
                      method: "POST",
                      credentials: "include",
                    });
                    if (!res.ok) {
                      const err = await res.json();
                      throw new Error(err.error || "Extraction failed");
                    }
                    const result = await res.json();
                    qc.invalidateQueries({ queryKey: ["/api/conversations", activeConversationId] });
                    const docLabel = result.documentType === "receivable_report" ? t("dean:docLabel.receivableReport") : t("dean:docLabel.statement");
                    toast({
                      title: t("dean:toast.extractedSaved"),
                      description: t("dean:toast.extractedSavedDesc", { doc: docLabel, lender: result.lenderName ? ` (${result.lenderName})` : "" }),
                    });
                    setShowExtractionPrompt(false);
                    onClose();
                    navigate("/floorplan");
                  } catch (err: any) {
                    toast({ title: t("dean:toast.extractionFailed"), description: err.message, variant: "destructive" });
                  } finally {
                    setExtracting(false);
                  }
                }}
                data-testid="button-run-extraction"
              >
                {extracting ? <Loader2 className="w-3 h-3 animate-spin" /> : null}
                {t("dean:extraction.extract")}
              </Button>
            </div>
          </div>
        )}

        {/* Input area — always shown */}
        <div className="px-3 pb-3 pt-1.5 shrink-0">
          <div className="flex gap-1.5 items-end rounded-xl border bg-background px-2.5 py-1.5 focus-within:ring-1 focus-within:ring-primary/30 transition-all">
            <input
              type="file"
              accept=".pdf"
              ref={fileInputRef}
              onChange={handleFileChange}
              className="hidden"
              data-testid="input-file-upload"
            />
            <Button
              variant="ghost"
              size="icon"
              className="shrink-0 h-7 w-7 text-muted-foreground hover:text-foreground"
              onClick={() => fileInputRef.current?.click()}
              disabled={composerDisabled}
              data-testid="button-attach-document"
              title={t("dean:input.attachTitle")}
            >
              {uploadingDoc ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
              ) : (
                <Paperclip className="w-3.5 h-3.5" />
              )}
            </Button>
            <Textarea
              ref={textareaRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={t(needsDealership ? "dean:dealershipRequired.inputPlaceholder" : "dean:input.placeholderShort")}
              className="flex-1 min-h-[32px] max-h-[100px] resize-none border-0 shadow-none focus-visible:ring-0 p-0 text-sm bg-transparent disabled:bg-muted disabled:opacity-60"
              disabled={composerDisabled}
              data-testid="input-message"
              rows={1}
            />
            <Button
              size="icon"
              className="shrink-0 h-7 w-7"
              onClick={handleSend}
              disabled={!input.trim() || composerDisabled}
              data-testid="button-send-message"
            >
              {isPending ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin" data-testid="dean-send-spinner" />
              ) : (
                <Send className="w-3.5 h-3.5" />
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
